import Icon from "@insite/mobius/Icon";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import Typography from "@insite/mobius/Typography";
import RichTooltip from "@thkStore/Components/Tooltip/RichTooltip";
import { CustomProperty } from "@thkStore/Types/CustomPropertiesByParentModel";
import React, { FC } from "react";
import styled from "styled-components";

const HelpIconWrapper = styled.span`
    display: inline-flex !important;
    height: auto !important;
    padding-top: 0 !important;
    margin-left: 10px;
    transform: translateY(4px);

    svg {
        color: ${({ theme }) => theme.colors.primary.main};
    }
`;

const HelpImage = styled.img`
    max-width: 100%;
    height: auto;
    margin-top: 8px;
    border-radius: 3px;
`;

const ItemText = styled(Typography)`
    display: inline-flex !important;
`;

/** Known custom property names for help content */
const HELP_PROPERTY_NAMES = {
    TEXT: "HelpText",
    IMAGE: "HelpImage",
} as const;

interface HelpContent {
    helpText?: string;
    helpImage?: string;
}

/**
 * Extracts help content (text and image) from a list of custom properties.
 *
 * @param customProperties - Array of custom property key-value pairs
 * @returns Help content object with optional helpText and helpImage, or null if none found
 */
export const getHelpContent = (customProperties: CustomProperty[]): HelpContent | null => {
    if (!customProperties || customProperties.length === 0) {
        return null;
    }

    const helpText = customProperties.find(p => p.name === HELP_PROPERTY_NAMES.TEXT)?.value;
    const helpImage = customProperties.find(p => p.name === HELP_PROPERTY_NAMES.IMAGE)?.value;

    if (!helpText && !helpImage) {
        return null;
    }

    return { helpText, helpImage };
};

interface ItemWithHelpInfoHelpProps {
    title: string;
    helpContent: CustomProperty[] | null;
}

export const ItemWithHelpInfoHelp: FC<ItemWithHelpInfoHelpProps> = ({ title, helpContent }) => {
    if (!helpContent) {
        return <>{title}</>;
    }

    const helpContentParsed = getHelpContent(helpContent);

    if (!helpContentParsed) {
        return <>{title}</>;
    }

    return (
        <>
            <ItemText>{title}</ItemText>
            <HelpIconWrapper>
                <RichTooltip 
                    trigger={<Icon src={HelpCircle} size={22} color="text.main" />}
                    position="bottom"
                >
                    {helpContentParsed.helpText && (
                        <Typography as="p" color="common.background">
                            {helpContentParsed.helpText}
                        </Typography>
                    )}
                    {helpContentParsed.helpImage && (
                        <HelpImage src={helpContentParsed.helpImage} alt={`Help for ${title}`} />
                    )}
                </RichTooltip>
            </HelpIconWrapper>
        </>
    );
};
